import { useCallback, useEffect, useState } from 'react';
import { api, ago, money, type FeedStatus, type VenueBalances, type VenueRouting } from '../lib/api.ts';

/**
 * Balances and feed health (FR-4.3).
 *
 * Two questions on one screen, because they are the two ways the tool goes
 * quietly wrong: is the data live, and is there still money on both sides?
 *
 * The second is the one that catches people out. In the pre-positioned model
 * every trade moves quote currency to one venue and base asset to the other,
 * so repeated trades in one direction drain one side. Phase 1 rebalances
 * manually, which only works if the drift is visible.
 */

interface Props {
  feeds: FeedStatus[];
  quoteCurrency: string;
  routing: VenueRouting[];
}

export function Balances({ feeds, quoteCurrency, routing }: Props) {
  const [balances, setBalances] = useState<VenueBalances[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.balances(refresh);
      setBalances(result.balances);
      setFetchedAt(result.fetchedAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load balances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const anyConfigured = balances.some((b) => b.configured);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Balances &amp; feeds</h1>
          <div className="sub">
            Both sides must stay funded on every venue — that is what makes a simultaneous two-leg
            trade possible without a transfer.
          </div>
        </div>
        <button className="btn small" onClick={() => void load(true)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Feed health</h2>
          <span className="faint" style={{ fontSize: 12 }}>
            a venue that is not live is excluded from opportunity calculation
          </span>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Venue</th>
                <th>State</th>
                <th className="num">Last update</th>
                <th className="num">Fresh pairs</th>
                <th className="num">Reconnects</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {feeds.length === 0 ? (
                <tr>
                  <td colSpan={6} className="dim" style={{ padding: 20 }}>
                    No feed data. Is the ingestor running?
                  </td>
                </tr>
              ) : (
                feeds.map((f) => {
                  const fresh = f.subscribedSymbols - f.staleSymbols;
                  return (
                    <tr key={f.exchange}>
                      <td>
                        <strong>{f.exchange}</strong>
                      </td>
                      <td>
                        <span className={`dot ${f.state}`} />
                        {f.state}
                      </td>
                      <td className="num">{ago(f.lastUpdateAt, now)}</td>
                      <td className={`num ${fresh === f.subscribedSymbols ? 'green' : 'amber'}`}>
                        {fresh}/{f.subscribedSymbols}
                      </td>
                      <td className={`num ${f.reconnects > 0 ? 'amber' : 'faint'}`}>{f.reconnects}</td>
                      <td className="faint" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {f.lastError ?? '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {error && <div className="notice bad">{error}</div>}

      {routing.length > 0 && (
        <div className="card">
          <div className="card-head">
            <h2>Transfer routing</h2>
            <span className="dim" style={{ fontSize: 12 }}>
              {routing.filter((r) => r.blocker === null).length} of {routing.length} venues fully usable
            </span>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Venue</th>
                  <th className="num">Key may withdraw</th>
                  <th className="num">Can send</th>
                  <th className="num">Can receive</th>
                  <th>What is stopping it</th>
                </tr>
              </thead>
              <tbody>
                {routing.map((r) => (
                  <tr key={r.exchange} style={r.blocker ? { opacity: 0.7 } : undefined}>
                    <td>
                      <strong>{r.label}</strong>
                    </td>
                    <td
                      className={`num ${r.keyCanWithdraw === false ? 'red' : r.keyCanWithdraw ? 'green' : 'amber'}`}
                      title={
                        r.keyCanWithdraw === null
                          ? 'this venue publishes no way to check — confirm it in the exchange yourself'
                          : ''
                      }
                    >
                      {r.keyCanWithdraw === false ? 'NO' : r.keyCanWithdraw ? 'yes' : 'unknown'}
                    </td>
                    <td className={`num ${r.canSend ? 'green' : 'red'}`}>
                      {r.canSend ? `${r.sendableCoins} coins` : 'no'}
                    </td>
                    <td className={`num ${r.canReceive ? 'green' : 'red'}`}>
                      {r.canReceive ? `${r.receivableCoins} coins` : 'no'}
                    </td>
                    <td className="faint">{r.blocker ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="faint" style={{ fontSize: 12, lineHeight: 1.6, marginTop: 12 }}>
            A key marked <strong>NO</strong> can buy but not send, which fails after the purchase has
            gone through — that trade is refused before it starts. <strong>unknown</strong> means the
            venue offers no way to ask, so check it in the exchange yourself. A venue can only{' '}
            <strong>send</strong> coins we hold its own withdrawal fees for, and can only{' '}
            <strong>receive</strong> ones we hold a whitelisted deposit address for. Both ends are
            needed for a transfer, so a venue missing either is connected and idle rather than
            broken — and this says which half is missing, because the two look identical from the
            opportunities screen.
          </div>
        </div>
      )}

      {!anyConfigured && !loading && (
        <div className="notice warn">
          No exchange API keys are stored yet, so balances cannot be read. Add them in Settings,
          which states the permissions this deployment's method actually needs — they differ by
          method, and the wrong ones fail only once a trade is under way.
        </div>
      )}

      <div className="grid cols-2">
        {balances.map((venue) => (
          <div className="card" key={venue.exchange}>
            <div className="card-head">
              <h2>{venue.label}</h2>
              {!venue.configured ? (
                <span className="pill">no keys</span>
              ) : venue.ok ? (
                <span className="pill ok">connected</span>
              ) : (
                <span className="pill bad">error</span>
              )}
            </div>

            {!venue.configured ? (
              <div className="empty" style={{ padding: 24 }}>
                Add API keys in Settings to see balances here.
              </div>
            ) : !venue.ok ? (
              <div className="card-body">
                <div className="notice bad" style={{ margin: 0 }}>
                  {venue.error}
                  <div className="faint" style={{ marginTop: 8, fontSize: 12 }}>
                    Usually one of: the key lacks read permission, the venue requires an IP
                    allowlist entry for this server, or the server clock has drifted enough to
                    invalidate the request signature.
                  </div>
                </div>
              </div>
            ) : venue.balances.length === 0 ? (
              <div className="empty" style={{ padding: 24 }}>
                Connected, but this account holds nothing.
                <br />
                Cross-exchange arbitrage needs {quoteCurrency} <em>and</em> the traded coin here.
              </div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th className="num">Free</th>
                      <th className="num">In orders</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {venue.balances.slice(0, 12).map((b) => (
                      <tr key={b.asset}>
                        <td className="sym">{b.asset}</td>
                        <td className="num">{money(b.free, b.free < 1 ? 6 : 2)}</td>
                        <td className="num faint">{b.used > 0 ? money(b.used, 2) : '—'}</td>
                        <td className="num">{money(b.total, b.total < 1 ? 6 : 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {fetchedAt && (
        <div className="faint" style={{ fontSize: 12 }}>
          Balances fetched {ago(fetchedAt, now)}. Cached briefly — exchange rate limits are shared
          with the order path, and spending them on a screen refresh is how execution gets throttled.
        </div>
      )}
    </>
  );
}
